import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import appConfig from "../../appConfig";
import { getToken } from "../../services/apiService";
import { applyChanges, fits, optionFor, optionValue, segments, splitCodeString, valueError } from "./codeString";
import "./CodeStringBreakdown.css";

// Code items of each format, read once per visit (RDM changes rarely)
const formats = new Map();

const OTHER = "__other";
const CURRENT = "__current";

// Spaces would be invisible in a value
const shown = (value) => (value === "" ? "—" : value.replace(/ /g, "·"));

// Option column of a code item: its RDM options in a dropdown, or a field to type the value (fan size and other
// items without a list, or "Type another value..." in the dropdown)
const ItemControl = ({ item, value, onChange }) => {
  const [typing, setTyping] = useState(false);
  const option = optionFor(item, value);
  const hasOptions = item.options.length > 0;
  const showInput = !hasOptions || typing;
  const error = valueError(item, value);
  const label = `${item.codeItem}. ${item.description}`;
  // A value RDM doesn't list is only expected when the item accepts any value
  const offList = hasOptions && !option && !item.acceptAny;

  return (
    <div className="cs-control">
      {hasOptions && (
        <select
          className={`form-select form-select-sm${offList && !typing ? " cs-select--warning" : ""}`}
          aria-label={label}
          value={typing ? OTHER : option ? optionValue(item, option) : CURRENT}
          onChange={(e) => {
            if (e.target.value === OTHER) {
              setTyping(true);
              return;
            }
            setTyping(false);
            onChange(e.target.value);
          }}
        >
          {!option && <option value={CURRENT}>{shown(value)}{item.acceptAny ? "" : " - not an RDM option"}</option>}
          {item.options.map((o) => (
            <option key={o.value} value={optionValue(item, o)}>
              {o.value}{o.description ? ` - ${o.description}` : ""}
            </option>
          ))}
          <option value={OTHER}>Type another value...</option>
        </select>
      )}
      {showInput && (
        <input
          type="text"
          className={`form-control form-control-sm cs-mono${hasOptions ? " mt-1" : ""}${error ? " is-invalid" : ""}`}
          aria-label={hasOptions ? `${label}, typed value` : label}
          placeholder={`${item.length} character${item.length === 1 ? "" : "s"}`}
          maxLength={item.length}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={typing}
          spellCheck={false}
          autoComplete="off"
        />
      )}
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

// Modal that breaks a codestring into its code items, each with its options in a dropdown;
// Apply writes the new codestring back to the form
const ItemOption = ({ item, value }) => {
  const option = optionFor(item, value);
  if (option) return <span>{option.value}{option.description ? ` - ${option.description}` : ""}</span>;
  if (item.acceptAny || item.options.length === 0) return <span className="text-muted">Typed value</span>;
  return <span className="cs-option--warning">Not an RDM option</span>;
};

export default function CodeStringBreakdown({ show, formatNumber, codeString, onHide, onApply, readOnly = false }) {
  const { instance, accounts } = useMsal();
  const [format, setFormat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // { itemIndex: new value }
  const [changes, setChanges] = useState({});
  const [focused, setFocused] = useState(null);
  const [filter, setFilter] = useState("");
  const rowRefs = useRef({});

  const load = async () => {
    const cached = formats.get(formatNumber);
    if (cached) {
      setFormat(cached);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const accessToken = await getToken(instance, accounts);
      const response = await axios.get(`${appConfig.apiUrl}codestring/${formatNumber}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const result = response.data;
      if (result?.status !== "OK" || !result.data) {
        setError(result?.message || "The code items could not be loaded.");
        return;
      }
      formats.set(formatNumber, result.data);
      setFormat(result.data);
    } catch (err) {
      setError(err.response?.data?.message || "The code items could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    setChanges({});
    setFocused(null);
    setFilter("");
    setFormat(null);
    setError("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, formatNumber]);

  const items = format?.items || [];
  const originals = splitCodeString(codeString, items);
  const values = originals.map((value, index) => changes[index] ?? value);
  const newCodeString = applyChanges(codeString, items, changes);
  const changeCount = Object.keys(changes).length;
  const hasErrors = Object.entries(changes).some(([index, value]) => valueError(items[index], value));

  const meaning = (item, value) => optionFor(item, value)?.description || "";

  const text = filter.trim().toLowerCase();
  const visible = items
    .map((item, index) => ({ item, index }))
    .filter(
      ({ item, index }) =>
        !text ||
        String(item.codeItem) === text.replace(/\.$/, "") ||
        item.description.toLowerCase().includes(text) ||
        values[index].toLowerCase().includes(text) ||
        meaning(item, values[index]).toLowerCase().includes(text)
    );

  const change = (index, value) =>
    setChanges((prev) => {
      const next = { ...prev };
      if (value === originals[index]) delete next[index];
      else next[index] = value;
      return next;
    });

  // From the codestring at the top: go to that item and open its options
  const pick = (index) => {
    if (!visible.some((v) => v.index === index)) setFilter("");
    setTimeout(() => {
      const row = rowRefs.current[index];
      row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      if (!readOnly) row?.querySelector("select, input")?.focus({ preventScroll: true });
    }, 0);
  };

  const lengthNote =
    format && codeString.length !== format.length
      ? `This codestring has ${codeString.length} characters and format ${format.formatNumber} expects ${format.length}. ${
          codeString.length < format.length ? "Items past the end can't be changed." : "Check it is the right format."
        }`
      : "";

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable centered aria-labelledby="cs-title">
      <Modal.Header closeButton>
        <div>
          <Modal.Title id="cs-title" as="h5">CodeString breakdown</Modal.Title>
          <div className="text-muted small">
            Format {formatNumber}{format ? ` · ${items.length} code items` : ""}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="text-start pt-0">
        {loading && <div className="py-4 text-muted">Loading the code items of format {formatNumber}...</div>}
        {error && (
          <div className="alert alert-danger mt-3 d-flex justify-content-between align-items-center gap-2" role="alert">
            <span>{error}</span>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={load}>Retry</button>
          </div>
        )}
        {format && (
          <div role="table" aria-label="Code items">
            <div className="cs-sticky">
              <div className="cs-preview cs-mono" aria-label="CodeString">
                {segments(newCodeString, items).map((segment, i) =>
                  segment.index === null ? (
                    <span key={i} className="cs-segment cs-segment--loose">{segment.text}</span>
                  ) : (
                    <span
                      key={i}
                      className={`cs-segment${changes[segment.index] !== undefined ? " cs-segment--changed" : ""}${
                        focused === segment.index ? " cs-segment--selected" : ""
                      }`}
                      title={`${items[segment.index].codeItem}. ${items[segment.index].description}`}
                      onClick={() => pick(segment.index)}
                    >
                      {segment.text}
                    </span>
                  )
                )}
              </div>
              {lengthNote && <div className="cs-note">{lengthNote}</div>}
              <input
                type="search"
                className="form-control form-control-sm mt-2"
                placeholder="Find a code item by number, name, value or option"
                aria-label="Find a code item"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <div className="cs-row cs-row--head" role="row">
                <span role="columnheader">#</span>
                <span role="columnheader">Code item</span>
                <span role="columnheader">Value</span>
                <span role="columnheader">Option</span>
              </div>
            </div>

            {visible.map(({ item, index }) => {
              const editable = fits(codeString, item);
              const changed = changes[index] !== undefined;
              return (
                <div
                  key={`${item.codeItem}-${index}`}
                  ref={(el) => (rowRefs.current[index] = el)}
                  role="row"
                  className={`cs-row${changed ? " cs-row--changed" : ""}${focused === index ? " cs-row--focused" : ""}`}
                  onFocus={() => setFocused(index)}
                  onBlur={() => setFocused((current) => (current === index ? null : current))}
                >
                  <span role="cell" className="cs-row__number">{item.codeItem}.</span>
                  <span role="cell" className="cs-row__name">{item.description || "(no description)"}</span>
                  <span role="cell" className="cs-row__value cs-mono">
                    {changed && <s className="cs-row__was" title="Value in the codestring">{shown(originals[index])}</s>}
                    <span>{editable ? shown(values[index]) : "—"}</span>
                    {changed && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 cs-row__undo"
                        onClick={() => change(index, originals[index])}
                        aria-label={`Undo the change of ${item.codeItem}. ${item.description}`}
                      >
                        Undo
                      </button>
                    )}
                  </span>
                  <span role="cell" className="cs-row__control">
                    {editable && readOnly ? (
                      <ItemOption item={item} value={values[index]} />
                    ) : editable ? (
                      <ItemControl item={item} value={values[index]} onChange={(value) => change(index, value)} />
                    ) : (
                      <span className="text-muted small">Not in the codestring</span>
                    )}
                  </span>
                </div>
              );
            })}
            {!visible.length && <div className="text-muted small py-3">No code item matches "{filter}".</div>}
          </div>
        )}
      </Modal.Body>
      {readOnly ? (
        <Modal.Footer className="justify-content-between">
          <div className="small text-muted">Read only. To try another option, use Break down in a new test set.</div>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      ) : (
        <Modal.Footer className="justify-content-between">
          <div className="small text-muted">
            {changeCount ? `${changeCount} change${changeCount === 1 ? "" : "s"}` : "Pick another option or type a value"}
            {changeCount > 0 && (
              <button type="button" className="btn btn-sm btn-link py-0" onClick={() => setChanges({})}>
                Undo all
              </button>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
            <Button variant="primary" disabled={!changeCount || hasErrors} onClick={() => onApply(newCodeString)}>
              Apply
            </Button>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
}
